import { isEmpty, toNumber } from 'lodash';
import { object } from 'prop-types';
import { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { FormInput, RadioGroup, Skeleton } from 'components';
import { addUuid, convertToCents, formatLocalizedCurrency } from 'helpers';
import { useFetch, useSafeState, useUser } from 'hooks';
import { setCart, setLeisurePurchase, setTravelPurchase } from 'store';

const SubventionForm = ({ cart }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [hasMounted, setHasMounted] = useSafeState(false);
  const hasCalledSubvention = useRef(false);

  const {
    watch,
    setError,
    setValue,
    clearErrors,
    handleSubmit,
    formState: { errors, isValid }
  } = useFormContext();
  const [showSubventionAmount, setShowSubventionAmount] = useSafeState(false);
  const { currentUser } = useUser();
  const {
    call: subventionCall,
    data: subvention,
    setData: setSubvention,
    loading: subventionLoading,
    error: subventionError
  } = useFetch({ withSetState: false });
  const { call: subventionApplyCall, loading: subventionApplyLoading } = useFetch({ withSetState: false });
  const { call: subventionSaveCall } = useFetch({ withSetState: false });
  const typeItem = searchParams.get('typeItem');

  const storedSubvention = useSelector((state) =>
    typeItem ? state.travelPurchase.subvention : state.leisurePurchase.subvention
  );
  const subventionChoice = watch('validation.subventionChoice');

  const subventionAmount = toNumber(watch('validation.subventionAmount'));
  const [subventionOptions, setSubventionOptions] = useSafeState(
    addUuid(
      [
        {
          name: 'validation.subventionChoice',
          value: 'withSubvention',
          label: t('purchase:subvention.withSubvention')
        },
        {
          name: 'validation.subventionChoice',
          value: 'withoutSubvention',
          label: t('purchase:subvention.withoutSubvention')
        }
      ],
      'subvention-radio-'
    )
  );

  const applyBtnDisabled = useMemo(() => {
    return subventionLoading || subventionApplyLoading || !showSubventionAmount || (showSubventionAmount && !isValid);
  }, [subventionLoading, subventionApplyLoading, showSubventionAmount, subventionAmount, isValid]);

  // -----------------------------------------------------------------------------------------------------
  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  useEffect(() => {
    getSubvention();
  }, [cart]);

  /**
   * Disable option 'withSubvention' if subvention call failed
   * or if maximum subsidized amount available is 0
   */
  useEffect(() => {
    if (subventionError || subvention?.maximumSubsidized === 0) {
      const subventionsDisabled = subventionOptions.map((option) => {
        if (option.value === 'withSubvention') {
          return {
            ...option,
            disabled: true,
            ...(subvention?.maximumSubsidized === 0 && {
              content: () => (
                <div className="flex items-center flex-wrap gap-1">
                  <span className="line-through">{t('purchase:subvention.withSubvention')}</span>
                  <span>{t('purchase:subvention.noSubventionAvailable')}</span>
                </div>
              )
            })
          };
        }
        return option;
      });
      setSubventionOptions(subventionsDisabled);
    }
  }, [subventionError, subvention]);

  useEffect(() => {
    /**
     * Avoid calling autosolve and subsidized API on mounted
     * since there is a default value for subventionChoice
     */
    if (hasMounted) {
      if (subventionChoice === 'withoutSubvention') {
        setValue('validation.subventionValid', false);
        setValue('validation.subventionAmount', null);

        if (typeItem) {
          dispatch(setTravelPurchase({ key: 'subvention', value: null }));
        } else {
          dispatch(setLeisurePurchase({ key: 'subvention', value: null }));
        }
        validateSubvention();
      }
    } else {
      setHasMounted(true);
    }
  }, [subventionChoice]);

  // -----------------------------------------------------------------------------------------------------
  // Methods
  // -----------------------------------------------------------------------------------------------------
  const getSubvention = async () => {
    if (!currentUser || isEmpty(cart) || hasCalledSubvention.current) return;

    // Get subvention available for current user and cart content
    const { data } =
      (await subventionCall({
        service: 'subvention',
        method: 'post',
        path: `/api/${currentUser.businessId}/cart/subsidy/solve`,
        data: {
          employee: cart.employee,
          lines: (cart?.lines ?? []).map((line) => line.subsidizedInfo)
        }
      })) || {};

    if (!isEmpty(data?.body)) {
      hasCalledSubvention.current = true;
      setSubvention({
        ...data.body,
        // Convert price from cents to euros
        maximumSubsidized: toNumber(data.body.maximumSubsidized) / 100,
        minimumSubsidized: toNumber(data.body.minimumSubsidized) / 100
      });
    }

    // Pre-fill subvention amount if already stored in store
    if (storedSubvention) {
      setValue('validation.subventionChoice', 'withSubvention');
      setValue('validation.subventionAmount', storedSubvention / 100);
      setShowSubventionAmount(true);
    }
  };

  const toggleSubventionAmount = (event) => {
    const optionValue = event?.target?.value;
    setShowSubventionAmount(optionValue === 'withSubvention');
  };

  const validateSubvention = async () => {
    const min = subvention?.minimumSubsidized;
    const max = subvention?.maximumSubsidized;

    const handleApiResponse = async (selectedSubsidized) => {
      const { data } =
        (await subventionApplyCall(
          {
            service: 'subvention',
            method: 'post',
            path: `/api/${currentUser.businessId}/cart/subsidy/autosolve`,
            data: {
              employee: cart.employee,
              lines: (cart?.lines ?? []).map((line) => line.subsidizedInfo),
              selectedSubsidized
            }
          },
          { hideToast: true }
        )) || {};

      if (data?.status === 400) {
        setError('validation.subventionValid', {
          type: 'manual',
          message: t('purchase:subvention.error.invalid')
        });
        return null;
      } else if (data?.status === 200) {
        return data.body;
      }

      return null;
    };

    if (subventionChoice === 'withSubvention' && subventionAmount >= min && subventionAmount <= max) {
      clearErrors('validation.subventionAmount');
      clearErrors('validation.subventionValid');

      const autosolveResponse = await handleApiResponse(convertToCents(subventionAmount));

      if (autosolveResponse) {
        // Validate subvention in form to enable submit button
        setValue('validation.subventionValid', true);
        const subventionValue = convertToCents(subventionAmount);
        // Save subvention amount in store to display it in the summary
        dispatch(
          typeItem
            ? setTravelPurchase({ key: 'subvention', value: subventionValue })
            : setLeisurePurchase({ key: 'subvention', value: subventionValue })
        );
        toast.success(t('purchase:subvention.success'));
        saveSubventionInCart(autosolveResponse);
      }
    } else if (subventionChoice === 'withoutSubvention') {
      const autosolveResponse = await handleApiResponse(0);

      if (autosolveResponse) {
        // Save subvention amount in store to display it in the summary
        dispatch(
          typeItem
            ? setTravelPurchase({ key: 'subvention', value: 0 })
            : setLeisurePurchase({ key: 'subvention', value: 0 })
        );
        saveSubventionInCart(autosolveResponse);
      }
    }
  };

  const saveSubventionInCart = async (autosolveResponse) => {
    // Save subvention amount in cart after validation post
    if (isEmpty(currentUser) || isEmpty(cart)) return;
    const cartType = typeItem ? 'travel' : 'ticketing';

    const { data } =
      (await subventionSaveCall({
        service: 'checkout',
        method: 'post',
        path: `/${currentUser.serialNumber}/cart/${cartType}/subsidize`,
        data: {
          employee: {
            ...cart.employee,
            companyId: toNumber(currentUser.businessId)
          },
          lines: autosolveResponse?.cartLines?.map((line) => ({
            subsidizedId: line.id,
            selectedSubsidized: line.selectedSubsidized
          }))
        }
      })) || {};
    if (data?.status === 200) {
      dispatch(setCart(data.body));
    }
  };

  return (
    <>
      <h2 className="mb-6 underlined-blue after:hidden h3 lg:after:block lg:h2">{t('purchase:subvention.title')}</h2>

      <form onSubmit={handleSubmit(validateSubvention)} className="subvention-form">
        <RadioGroup
          radioOptions={subventionOptions}
          groupLabel={t('purchase:subvention.title')}
          labelHidden
          onChange={toggleSubventionAmount}
          showSlot="withSubvention"
          slot={() => (
            <>
              {/* SUBVENTION INPUT SKELETONS */}
              {subventionLoading && (
                <div>
                  <Skeleton classNames="w-80 h-8 mb-3" />
                  <Skeleton classNames="w-44 h-8 mb-2" />
                  <Skeleton classNames="w-44 h-8" />
                </div>
              )}

              {/* SUBVENTION CALL ERROR MESSAGE */}
              {!subventionLoading && subventionError && (
                <div className="text-gray-600 pl-8">{t('purchase:subvention.error.loading')}</div>
              )}

              {!subventionLoading && !isEmpty(subvention) && subvention?.maximumSubsidized > 0 && (
                <div className="pl-4">
                  <div className="flex items-center gap-4 mb-2">
                    <FormInput
                      name="validation.subventionAmount"
                      placeholder={t('purchase:subvention.amount')}
                      type="number"
                      disabled={subventionChoice !== 'withSubvention'}
                      rules={{
                        required: subventionChoice === 'withSubvention',
                        min: {
                          value: subvention?.minimumSubsidized || 1,
                          message: t('purchase:subvention.error.min', {
                            min: formatLocalizedCurrency(subvention?.minimumSubsidized)
                          })
                        },
                        max: {
                          value: subvention?.maximumSubsidized,
                          message: t('purchase:subvention.error.max', {
                            max: formatLocalizedCurrency(subvention?.maximumSubsidized)
                          })
                        }
                      }}
                    />

                    <button type="sumbit" className="btn secondary text-sm md:text-base" disabled={applyBtnDisabled}>
                      {t('buttons.apply')}
                    </button>
                  </div>
                  {errors?.validation?.subventionAmount?.message && (
                    <p className="text-red-500 my-2">{errors?.validation?.subventionAmount?.message}</p>
                  )}

                  {errors?.validation?.subventionValid?.message && (
                    <p className="text-red-500 my-2">{errors?.validation?.subventionValid?.message}</p>
                  )}

                  <p>
                    {t('minimum')}:&nbsp;{formatLocalizedCurrency(subvention?.minimumSubsidized || 1)}
                  </p>
                  <p>
                    {t('maximum')}:&nbsp;{formatLocalizedCurrency(subvention?.maximumSubsidized)}
                  </p>
                </div>
              )}
            </>
          )}
        />
      </form>
    </>
  );
};

SubventionForm.propTypes = {
  cart: object
};

export default SubventionForm;
